<template>
  <div class="flex flex-col min-h-screen items-center">
    <div class="flex justify-between items-center w-full max-w-[1280px]">
      <div class="ml-4 sm:ml-0">
        <LogoIcon />
      </div>
      <div class="h-12 mt-5 mb-5 mr-4 sm:mr-8">
        <LanguageSwitch />
      </div>
    </div>
    <div class="max-w-[460px] w-full text-center flex-grow px-4">
      <h2 class="mt-5 mt-10 mb-6 text-center">
        {{ $t('messages.entra_sign_in.title') }}
      </h2>
      <label
        for="domain"
        class="b2 block text-left w-full mb-2 text-zyxel-gray-800"
      >{{ $t('messages.entra_sign_in.domain') }}</label>
      <div class="relative w-full">
        <div class="absolute left-0 top-0 h-12 flex items-center justify-center w-8 text-zyxel-gray-600">
          @
        </div>
        <input
          id="domain"
          v-model="domain"
          type="text"
          :class="['text-input pt-4 pl-10 pb-4 text-left h-12 w-full', { 'error-input': errors.domain }]"
          placeholder="example.com"
          @keyup.enter="handleSubmit"
        >
      </div>
      <span
        v-if="errors.domain"
        class="error-message mt-1 b2 block text-left w-full"
        role="alert"
        aria-live="assertive"
      >{{
        $t(errors_msg.domain) }}</span>
      <div class="flex justify-between items-center mt-6">
        <div class="flex items-center mycheckbox">
          <div class="flex items-center cursor-pointer">
            <input
              id="remember_me"
              v-model="rememberMe"
              type="checkbox"
              class="opacity-0 absolute h-8 w-8"
            >
            <div
              :class="[{ 'border-zyxel-green-500': rememberMe, 'border-gray-800': !rememberMe }, 'border-2 w-6 h-6 rounded-md']"
            />
            <label
              for="remember_me"
              class="ml-2 b1 text-zyxel-gray-800"
            >{{ $t('messages.sign_in.checkbox_remember_me')
            }}</label>
          </div>
        </div>
      </div>
      <button
        class="button-primary w-full mt-4 mb-3 focus:outline-none focus:ring-2 focus:ring-zyxel-green-500"
        @click="handleSubmit"
      >
        {{ $t('messages.button.continue')
        }}
      </button>
      <div class="mt-4 mb-6">
        <router-link
          class="b1 text-zyxel-green-700"
          :to="{ name: 'SignIn' }"
        >
          {{ $t('messages.common.back_to_home') }}
        </router-link>
      </div>
    </div>
    <ZyxelFooter />
  </div>
</template>
<script setup>
/* global grecaptcha */
import { ref, onMounted } from 'vue';
import LogoIcon from '@/components/LogoIcon.vue';
import LanguageSwitch from "@/components/LanguageSwitch.vue";
import ZyxelFooter from "@/components/ZyxelFooter.vue";
import axios from 'axios';
import { apiBaseUrl, recaptchaSitekey, recaptchaVerifyFlag } from '@/config';
import { useRouter } from 'vue-router';
import { logger } from '@/utils/logger';

const router = useRouter();

// State variables
const domain = ref('');
const rememberMe = ref(false);
const errors = ref({
    domain: false
});
const errors_msg = ref({
    domain: false
});
const isScriptLoaded = ref(false);

// Method to load external scripts
const loadScript = (src, callback) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = callback;
    document.head.appendChild(script);
};

// Load reCAPTCHA script
if (!isScriptLoaded.value) {
    loadScript('https://www.google.com/recaptcha/enterprise.js?render=' + recaptchaSitekey, () => {
        isScriptLoaded.value = true;
    });
}

// Utility functions
const isValidDomain = (domain) => {
    const pattern = /^(?:(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)\.)+[a-zA-Z]{2,}$/;
    return pattern.test(domain);
};

const checkInputs = () => {
    let valid = true;
    errors.value.domain = false;
    errors_msg.value.domain = false;

    if (domain.value.trim().length === 0) {
        errors.value.domain = true;
        errors_msg.value.domain = "messages.error_msg.field_empty";
        valid = false;
    } else if (domain.value.trim().length > 250) {
        errors.value.domain = true;
        errors_msg.value.domain = "messages.error_msg.length_250";
        valid = false;
    } else if (!isValidDomain(domain.value.trim())) {
        errors.value.domain = true;
        errors_msg.value.domain = "messages.error_msg.domain_not_valid";
        valid = false;
    }
    return valid;
};


// Main functionality
const handleEntraSignIn = async () => {
    if (rememberMe.value) {
        localStorage.setItem('rememberedDomain', domain.value.trim());
    } else {
        localStorage.removeItem('rememberedDomain');
    }

    const urlWithQuery = `${apiBaseUrl}/api/entra_id/sign_in?domain=${domain.value}`;
    try {
        const response = await fetch(urlWithQuery, {
            method: 'GET',
            credentials: 'include',
        });
        if (!response.ok) {
          errors.value.domain = true;
          errors_msg.value.domain = "messages.error_msg.domain_not_valid";
          return;
        }

        // not get exception
        const resJson = await response.json();
        const redirectUrl = resJson.redirect_url;
        window.location.href = redirectUrl;

    } catch (error) {
        logger.error('entra sign_in error:', error);
    }
};

const handleSubmit = () => {
    if (!checkInputs()) {
        return;
    }

    const action_event = 'entra_sign_in';

    // Launch reCAPTCHA and perform verification
    grecaptcha.enterprise.ready(async () => {
        try {
            const token = await grecaptcha.enterprise.execute(
                recaptchaSitekey, { action: action_event }
            );

            const response = await axios.post(`${apiBaseUrl}/api/recaptcha/token`, {
                token: token,
                action: action_event
            });

            logger.log('score:', response.data.score);
            logger.log('reason:', response.data.reason);

            if (recaptchaVerifyFlag) {
                const success = response.data.success;
                const score = response.data.score;
                if (success && score > 0.5) {
                    handleEntraSignIn();
                } else {
                    router.push({ name: 'RobotEndpoint' });
                }
            } else {
                handleEntraSignIn();
            }
        } catch (error) {
            logger.error('Error:', error);
        }
    });
};

// Lifecycle hooks
onMounted(() => {
    // Check if there's a remembered domain
    const rememberedDomain = localStorage.getItem('rememberedDomain');
    if (rememberedDomain) {
        domain.value = rememberedDomain;
        rememberMe.value = true;
    }
});
</script>